// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Card from 'antd/lib/card';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import Input from 'antd/lib/input';
import { MoreOutlined } from '@ant-design/icons';

import { getCore } from 'cvat-core-wrapper';
import { CombinedState, LabelingRunData } from 'reducers';
import {
    deleteLabelingRunAsync,
    updateJobQAStateAsync,
    updateLabelingRunAsync,
    duplicateLabelingRunAsync,
} from 'actions/labeling-runs-actions';

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)(${name})=([^;]*)`));
    return match ? decodeURIComponent(match[3]) : null;
}

async function apiFetch(path: string): Promise<any> {
    const response = await fetch(path, {
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') || '' },
        credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`${response.status}`);
    return response.json();
}

interface Props {
    run: LabelingRunData;
}

const core = getCore();

export default function RunItemComponent({ run }: Props): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();
    const updated = moment(run.updated_date).fromNow();
    const ownerName = run.owner ? run.owner.username : null;
    const totalJobs = run.total_jobs ?? 0;
    const [doneJobs, setDoneJobs] = useState<number>(run.done_jobs ?? 0);
    const [renameVisible, setRenameVisible] = useState(false);
    const [renameName, setRenameName] = useState(run.name);
    const [dupVisible, setDupVisible] = useState(false);
    const [dupName, setDupName] = useState('');
    const currentUser = useSelector((state: CombinedState) => state.auth.user);
    const canManage = !!currentUser && (
        currentUser.isSuperuser || currentUser.isStaff || currentUser.groups.includes('admin')
    );

    useEffect(() => {
        setDoneJobs(run.done_jobs ?? 0);
    }, [run.done_jobs]);

    useEffect(() => {
        // Auto-sync in background: check CVAT job states and update QA states
        Promise.all([
            apiFetch(`/api/labeling-runs/${run.id}/tasks`),
            apiFetch(`/api/labeling-runs/${run.id}/qa-states`),
        ]).then(([tasks, qaStatesList]: [any[], any[]]) => {
            const qaMap: Record<number, string> = {};
            qaStatesList.forEach((s: any) => { qaMap[s.job_id] = s.state; });

            Promise.all(
                tasks.map((t: any) => core.tasks.get({ id: t.task_id })
                    .then(([ti]: any[]) => ti)
                    .catch(() => null)),
            ).then((taskInstances: any[]) => {
                taskInstances.forEach((ti: any) => {
                    if (!ti) return;
                    ti.jobs.forEach((job: any) => {
                        const currentQA = qaMap[job.id] || 'new';
                        if (job.stage === 'annotation' && job.state === 'in progress' && currentQA === 'new') {
                            qaMap[job.id] = 'labeling';
                            dispatch(updateJobQAStateAsync(run.id, job.id, 'labeling') as any);
                        } else if (job.stage === 'acceptance' && job.state === 'completed' &&
                                   (currentQA === 'new' || currentQA === 'labeling')) {
                            qaMap[job.id] = 'done';
                            dispatch(updateJobQAStateAsync(run.id, job.id, 'done') as any);
                        }
                    });
                });
                setDoneJobs(Object.values(qaMap).filter((s: string) => s === 'done').length);
            }).catch(() => {});
        }).catch(() => {});
    }, [run.id]);

    const allDone = totalJobs > 0 && doneJobs === totalJobs;
    const coverBg = allDone ? '#f6ffed' : '#fafafa';
    const coverColor = allDone ? '#52c41a' : undefined;

    const cover = (
        <div
            style={{
                height: 160,
                background: coverBg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text strong style={{ fontSize: 32, color: coverColor }}>
                {`${doneJobs} / ${totalJobs}`}
            </Text>
            <Text type='secondary' style={{ fontSize: 12 }}>jobs done</Text>
        </div>
    );

    const menu = (
        <Menu>
            <Menu.Item
                key='rename'
                onClick={(e) => { e.domEvent.stopPropagation(); setRenameName(run.name); setRenameVisible(true); }}
            >
                Rename
            </Menu.Item>
            <Menu.Item
                key='duplicate'
                onClick={(e) => { e.domEvent.stopPropagation(); setDupName(`${run.name} (copy)`); setDupVisible(true); }}
            >
                Duplicate
            </Menu.Item>
            {canManage && (
                <Menu.Item
                    key='delete'
                    danger
                    onClick={(e) => { e.domEvent.stopPropagation(); dispatch(deleteLabelingRunAsync(run.id) as any); }}
                >
                    Delete
                </Menu.Item>
            )}
        </Menu>
    );

    return (
        <>
        <Modal
            title='Rename Run'
            open={renameVisible}
            onCancel={(e) => { e.stopPropagation(); setRenameVisible(false); }}
            onOk={(e) => {
                e.stopPropagation();
                if (renameName.trim()) dispatch(updateLabelingRunAsync(run.id, renameName.trim()) as any);
                setRenameVisible(false);
            }}
        >
            <Input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onPressEnter={() => {
                    if (renameName.trim()) dispatch(updateLabelingRunAsync(run.id, renameName.trim()) as any);
                    setRenameVisible(false);
                }}
            />
        </Modal>
        <Modal
            title='Duplicate Run'
            open={dupVisible}
            onCancel={(e) => { e.stopPropagation(); setDupVisible(false); }}
            onOk={(e) => {
                e.stopPropagation();
                if (dupName.trim()) dispatch(duplicateLabelingRunAsync(run.id, dupName.trim()) as any);
                setDupVisible(false);
            }}
        >
            <Input
                value={dupName}
                onChange={(e) => setDupName(e.target.value)}
                onPressEnter={() => {
                    if (dupName.trim()) dispatch(duplicateLabelingRunAsync(run.id, dupName.trim()) as any);
                    setDupVisible(false);
                }}
            />
        </Modal>
        <Card
            size='small'
            hoverable
            cover={cover}
            className='cvat-labeling-run-item-card'
            onClick={() => history.push(`/labeling-runs/${run.id}`)}
            style={{ margin: 8 }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Text strong ellipsis style={{ maxWidth: 160, display: 'block' }}>
                        {run.name}
                    </Text>
                    {ownerName && (
                        <Text type='secondary' style={{ fontSize: 12 }}>
                            {`by ${ownerName}`}
                        </Text>
                    )}
                    <br />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                        {`Updated ${updated}`}
                    </Text>
                </div>
                <Dropdown
                    overlay={menu}
                    trigger={['click']}
                    destroyPopupOnHide
                >
                    <Button
                        type='link'
                        size='large'
                        icon={<MoreOutlined />}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Dropdown>
            </div>
        </Card>
        </>
    );
}
